import { Graphics, Rectangle, Sprite, Texture } from 'pixi.js';
import type { StatusApply } from '../core/types';
import { FX_RAMPS, type BurstStyle } from '../core/skillfx';
import { audio } from '../core/audio';
import { loadSheet } from './spirits';
import type { WeaponHost } from './weapons';

/**
 * 【고유】 광풍 — 네 빛깔 회오리.
 *
 * 그림: public/assets/fx/tempest.png (tools/build_fx.py --tempest 가 받은 시트의 회오리 기둥을 **깔때기**로 다시 감아 돌아가는 장면을 굽는다)
 *
 * 불·얼음·번개·모래 회오리 넷이 캐릭터 둘레를 휘몰아친다. 넷은 늘 90°씩 벌어져 돌고, 마주 보는 둘씩 번갈아 부풀었다 오므라든다(숨 쉬듯).
 *   불 [화염] · 얼음 [서리] · 번개 [감전] · 모래는 깔때기로 끌어당기고 느리게 한다 — 둘 이상에 휘말리면 원소 반응이 저절로 난다.
 * 몇 초마다(pulse) **조화**: 넷이 소용돌이치며 캐릭터 곁으로 모여(기운이 모인 그림, 머리가 안으로 기울고 번개가 넷을 잇는다) 빠르게 돌다가
 * 한꺼번에 터진다 — 네 빛깔 고리가 퍼지며 둘레 전부에 불·서리·전하를 한꺼번에 씌우고 날려 보낸 뒤, 절정 그림으로 제자리까지 튀어 나간다.
 *
 * 한 회오리의 모양 (1.21 다시 깎음 — 예전엔 뭉툭한 통이었다):
 *   깔때기 몸통(띠 BANDS 개로 나눠 발끝은 땅에 박고 머리 쪽만 휘게) · 발끝의 도는 소용돌이 원반 · 몸통을 감는 바람 줄 셋(앞은 짙게, 뒤는 옅게) ·
 *   몸통에 휘말려 올라갔다 튕겨 나가는 파편(불티·얼음 조각·불똥·모래 알갱이) · 발끝을 납작하게 도는 흙먼지 치마.
 */

export type Gust = 'fire' | 'ice' | 'bolt' | 'sand';

/** 회오리에 휘말린 적: 이만큼 떠올라(px) 이 시간(초) 동안 한 바퀴 — 같은 적은 GUST_LIFT_CD 초에 한 번만 */
const GUST_LIFT_H = 34;
const GUST_LIFT_T = 0.5;
const GUST_LIFT_CD = 1.4;
/** 조화에 휩쓸린 적: 더 높이, 더 오래, 두 바퀴 */
const BURST_LIFT_H = 100;
const BURST_LIFT_T = 0.85;
export const GUSTS: readonly Gust[] = ['fire', 'ice', 'bolt', 'sand'];

/** 깔때기 한 장을 가로 띠로 잘라 띠마다 따로 민다(위로 갈수록 크게) — 발끝은 박힌 채 머리가 휜다 */
const BANDS = 12;

interface ColFrame {
  full: Texture;
  w: number;
  h: number;
  ax: number;
  ay: number;
  bands: Array<{ tex: Texture; top: number }>;
}
interface PieceFrame {
  tex: Texture;
  ax: number;
  ay: number;
}

/** 색 → 변형(0 보통 · 1 기운이 모인 · 2 절정) → 도는 장면 */
const columns = new Map<Gust, ColFrame[][]>();
/** 흙먼지 회오리: 색 → 조각 → 장면 */
const devilArt = new Map<Gust, PieceFrame[][]>();
/** 발끝 소용돌이 원반: 색 → 도는 장면 */
const baseArt = new Map<Gust, PieceFrame[]>();
const DEVIL_PIECES: Record<Gust, string[]> = {
  fire: ['devil_fire', 'disc_fire'],
  ice: ['disc_ice'],
  bolt: ['spark_bolt', 'disc_bolt'],
  sand: ['disc_sand'],
};
const BASE_PIECE: Record<Gust, string> = { fire: 'disc_fire', ice: 'disc_ice', bolt: 'disc_bolt', sand: 'disc_sand' };

type FrameJson = { x: number; y: number; w: number; h: number; ax: number; ay: number };

/** 광풍 그림을 읽는다. 못 읽으면 광풍은 예전 소용돌이 베기(melee)로 돈다 */
export async function loadTempestArt(): Promise<void> {
  try {
    const { meta, source } = await loadSheet('assets/fx/tempest.json');
    const frames = meta.frames as Record<string, FrameJson>;
    const spin = (meta.spin as number) ?? 10;
    const piece = (name: string): PieceFrame[] => {
      const list: PieceFrame[] = [];
      for (let k = 0; frames[`${name}_${k}`]; k++) {
        const f = frames[`${name}_${k}`];
        list.push({ tex: new Texture({ source, frame: new Rectangle(f.x, f.y, f.w, f.h) }), ax: f.ax / f.w, ay: f.ay / f.h });
      }
      return list;
    };
    for (const g of GUSTS) {
      const vars: ColFrame[][] = [];
      for (let v = 0; v < 3; v++) {
        const list: ColFrame[] = [];
        for (let k = 0; k < spin; k++) {
          const f = frames[`${g}_${v}_${k}`];
          if (!f) break;
          const bands: ColFrame['bands'] = [];
          for (let b = 0; b < BANDS; b++) {
            const top = Math.floor((f.h * b) / BANDS);
            const bot = Math.floor((f.h * (b + 1)) / BANDS);
            bands.push({ tex: new Texture({ source, frame: new Rectangle(f.x, f.y + top, f.w, bot - top) }), top });
          }
          list.push({ full: new Texture({ source, frame: new Rectangle(f.x, f.y, f.w, f.h) }), w: f.w, h: f.h, ax: f.ax, ay: f.ay, bands });
        }
        if (list.length === 0) throw new Error(`${g}_${v} 그림이 없다`);
        vars.push(list);
      }
      columns.set(g, vars);
      devilArt.set(g, DEVIL_PIECES[g].map(piece).filter((l) => l.length > 0));
      const base = piece(BASE_PIECE[g]);
      if (base.length) baseArt.set(g, base);
    }
  } catch (e) {
    columns.clear();
    devilArt.clear();
    baseArt.clear();
    console.warn('[art] 광풍 그림을 못 읽었다. 예전 소용돌이 베기로 대신한다.', e);
  }
}

/** 도감용: 색·변형마다 첫 장 */
export function tempestTextures(): Texture[] {
  const out: Texture[] = [];
  for (const g of GUSTS) for (const v of columns.get(g) ?? []) if (v[0]) out.push(v[0].full);
  return out;
}

// ── 박자 (게임 시간, 초) ─────────────────────────────────

/** 조화 전에 모여드는 시간 (pulse 끝에서 거꾸로) — 이 가운데 끝 CHARGE 초는 다 모여 몸 가까이서 빠르게 돈다 */
const GATHER = 1.0;
const CHARGE = 0.28;
/** 터진 뒤 절정 그림으로 튀어 나갔다가 제자리로 돌아오는 시간 */
const FLING = 0.9;
/** 다 모였을 때 캐릭터와의 거리 */
const IN_R = 46;
/** 도는 빠르기(rad/s): 보통 · 터진 직후 · 모여 돌 때 최고 */
const W0 = 1.9;
const W_FLING = 5;
const WMAX = 7.4;
/** 피해 배율: 회오리 한 번 닿을 때 · 조화가 터질 때 (무기 피해 기준) */
const TWISTER_MUL = 1.3;
const BURST_MUL = 3.6;
/** 조화가 터지는 반경 = 궤도 반경 × 이만큼 */
const BURST_R = 1.3;

const SAND_RAMP = ['#fff4e0', '#ead3a8', '#c59d6b', '#8c6842', '#4a3524'] as const;
// 번개는 밝은 여제 금빛(royal) — 보통 금빛(gold)은 모래와 너무 닮아 빛깔이 섞여 보였다
const RAMP: Record<Gust, readonly string[]> = { fire: FX_RAMPS.flame, ice: FX_RAMPS.ice, bolt: FX_RAMPS.royal, sand: SAND_RAMP };
const hex = (s: string): number => parseInt(s.slice(1), 16);
const TINT: Record<Gust, number[]> = {
  fire: RAMP.fire.map(hex),
  ice: RAMP.ice.map(hex),
  bolt: RAMP.bolt.map(hex),
  sand: RAMP.sand.map(hex),
};
/** 회오리 밑동에서 튀는 것 (불티 · 서리 · 불똥 · 모래 바람) */
const PUFF: Record<Gust, { attr: string; style: BurstStyle }> = {
  fire: { attr: 'red', style: 'ember' },
  ice: { attr: 'blue', style: 'spark' },
  bolt: { attr: 'yellow', style: 'spark' },
  sand: { attr: 'brown', style: 'air' },
};
/** 몸통에 휘말려 도는 파편 빛깔 (밝음 → 어두움) — 모래는 굵은 돌도 섞인다 */
const DEBRIS: Record<Gust, number[]> = {
  fire: [0xfff1b8, 0xffb347, 0xff6a1f, 0xc2300c],
  ice: [0xffffff, 0xd8f4ff, 0x9fe8ff, 0x5ab8e8],
  bolt: [0xffffff, 0xfff3b0, 0xffd23a, 0xb98a10],
  sand: [0xead3a8, 0xc59d6b, 0x8c6842, 0x4a3524],
};

const TAU = Math.PI * 2;
const QUARTER = Math.PI / 2;
const smooth = (u: number): number => {
  const v = Math.min(1, Math.max(0, u));
  return v * v * (3 - 2 * v);
};
const easeOut = (u: number): number => 1 - (1 - Math.min(1, Math.max(0, u))) ** 3;

/** 숨쉬기: 궤도 반경이 이만큼(비율) 안팎으로 오간다 */
const BREATHE = 0.34;
/** 깔때기 그림의 크기 기준 — 회오리 하나가 닿는 반경(size)이 이만큼일 때 원래 크기. 「폭풍의 눈」·범위 패시브로 커지면 같이 커진다 */
const SIZE_REF = 60;
/** 깔때기 몸통의 모양 (tools/build_fx.py 의 TEMPEST_TIP·CURVE 와 같게) — 높이 up(발끝 0 → 머리 1)에서 머리 반지름에 대한 비율 */
const FUNNEL_TIP = 0.17;
const FUNNEL_CURVE = 2.1;
const funnelK = (up: number): number => FUNNEL_TIP + (1 - FUNNEL_TIP) * up ** FUNNEL_CURVE;

/** 몸통을 감는 바람 줄: 높이(발끝 0 → 머리 1) · 길이(rad) · 빠르기 배율 */
const WISPS: ReadonlyArray<{ up: number; span: number; speed: number }> = [
  { up: 0.3, span: 3.6, speed: 1.25 },
  { up: 0.58, span: 4.0, speed: 1.0 },
  { up: 0.86, span: 3.3, speed: 0.85 },
];
/** 파편: 회오리 하나에 초당 몇 개, 한꺼번에 최대. 이 가운데 SKIRT 비율은 발끝 둘레를 납작하게 도는 흙먼지 치마 */
const DEBRIS_RATE = 34;
const DEBRIS_MAX = 52;
const SKIRT = 0.45;

interface Particle {
  /** 몸통 둘레 각 · 높이(발끝 0 → 머리 1) · 몸통 반지름에 곱하는 것 */
  th: number;
  up: number;
  rk: number;
  /** 각속도 · 오르는 빠르기(초당 up) · 반지름이 느는 빠르기 */
  w: number;
  vu: number;
  vr: number;
  t: number;
  life: number;
  color: number;
  size: number;
  /** 발끝 흙먼지 치마 (오르지 않고 납작하게 돈다, 조금 옅다) */
  skirt: boolean;
}

interface Twister {
  el: Gust;
  i: number;
  bands: Sprite[];
  /** 몸통 뒤·앞에 그리는 바람 줄과 파편 */
  back: Graphics;
  front: Graphics;
  base: Sprite;
  shadow: Graphics;
  x: number;
  y: number;
  spinT: number;
  baseT: number;
  puff: number;
  wispA: number;
  debris: Particle[];
  debrisAcc: number;
  /** 이번 장면의 몸통: 높이(px) · 머리 반지름(px) · 띠마다 가로로 민 양(머리 쪽) */
  h: number;
  rTop: number;
  bend: number[];
  alpha: number;
}

interface Devil {
  sprite: Sprite;
  frames: PieceFrame[];
  t: number;
  life: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  scale: number;
}

export interface TempestParams {
  dmg: number;
  cd: number;
  slot: number;
  /** 궤도 반경 (범위 배율 포함) */
  radius: number;
  /** 회오리 하나가 닿는 반경 (범위 배율 포함) */
  size: number;
  knockback: number;
  /** 조화 간격(초) */
  pulse: number;
  /** 무기의 상태효과 전부 (플레이어 보너스 포함) — 조화 때 한꺼번에 씌운다 */
  status: StatusApply;
}

/** 호를 점으로 (가로 반지름 rx, 세로 ry). Graphics.arc 는 쓰지 않는다 — 시작점을 따로 안 찍으면 앞 도형의 끝(비었으면 월드 원점)에서
 *  선을 그어 와서, 판 시작 자리까지 레이저 같은 선이 날아갔다(1.21 에 찾은 버그) */
function arcPts(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, n: number): number[] {
  const pts: number[] = [];
  for (let k = 0; k <= n; k++) {
    const a = a0 + ((a1 - a0) * k) / n;
    pts.push(cx + Math.cos(a) * rx, cy + Math.sin(a) * ry);
  }
  return pts;
}

export class TempestStorm {
  private readonly twisters: Twister[] = [];
  private readonly devils: Devil[] = [];
  private readonly ground: Graphics;
  private readonly air: Graphics;
  private host: WeaponHost;
  private time = 0;
  /** 조화 박자 안에서 흐른 시간 — 0 에서 시작하면 캐릭터 몸에서 튀어 나오며 등장한다 */
  private cyc = 0;
  private angle = 0;
  private devilT = 0;
  private pullT = 0;
  /** 조화 번개: 다시 그을 때까지 · 줄기마다 꺾이는 모양(0~1 사이 흔들림) */
  private arcT = 0;
  private arcShape: number[][] = [];
  /** 조화 고리가 퍼지는 중이면 0 이상 */
  private ringT = -1;
  private ringR = 0;
  /** 그림 크기 배율 (회오리가 닿는 반경을 따라) */
  private vis = 1;

  static available(): boolean {
    return GUSTS.every((g) => columns.has(g));
  }

  constructor(host: WeaponHost) {
    this.host = host;
    this.ground = new Graphics();
    host.groundLayer.addChild(this.ground);
    this.air = new Graphics();
    host.fxLayer.addChild(this.air);
    GUSTS.forEach((el, i) => {
      const bands: Sprite[] = [];
      const back = new Graphics();
      host.actorLayer.addChild(back);
      for (let b = 0; b < BANDS; b++) {
        const sp = new Sprite();
        sp.visible = false;
        host.actorLayer.addChild(sp);
        bands.push(sp);
      }
      const front = new Graphics();
      host.actorLayer.addChild(front);
      const shadow = new Graphics();
      shadow.ellipse(0, 0, 40, 11).fill({ color: 0x000000, alpha: 0.22 });
      shadow.ellipse(0, 0, 24, 7).fill({ color: 0x000000, alpha: 0.18 });
      host.groundLayer.addChild(shadow);
      const base = new Sprite();
      base.visible = false;
      host.groundLayer.addChild(base);
      this.twisters.push({
        el, i, bands, back, front, base, shadow, x: host.px, y: host.py, spinT: i * 2.5, baseT: i * 3,
        puff: i * 0.1, wispA: i * 1.3, debris: [], debrisAcc: 0, h: 0, rTop: 0, bend: [], alpha: 1,
      });
    });
    for (let k = 0; k < 8; k++) {
      const sp = new Sprite();
      sp.visible = false;
      host.fxLayer.addChild(sp);
      this.devils.push({ sprite: sp, frames: [], t: 0, life: 0, x: 0, y: 0, vx: 0, vy: 0, scale: 1 });
    }
  }

  destroy(): void {
    for (const t of this.twisters) {
      for (const b of t.bands) b.destroy();
      t.back.destroy();
      t.front.destroy();
      t.base.destroy();
      t.shadow.destroy();
    }
    this.twisters.length = 0;
    for (const d of this.devils) d.sprite.destroy();
    this.devils.length = 0;
    this.ground.destroy();
    this.air.destroy();
  }

  /** 한 스텝. 맞힌 수를 돌려준다 */
  update(host: WeaponHost, dt: number, p: TempestParams): number {
    this.host = host;
    this.time += dt;
    this.vis = Math.max(0.9, Math.min(1.35, p.size / SIZE_REF));
    const P = Math.max(2.4, p.pulse || 4);
    const R = p.radius;
    const g0 = P - GATHER;
    const c0 = this.cyc;
    this.cyc += dt;
    let hits = 0;
    let burst = false;
    if (this.cyc >= P) {
      this.cyc -= P;
      burst = true;
    }
    const c = this.cyc;
    if (c0 < g0 && c >= g0) audio.play('breeze', { rate: 0.55, gain: 1.5 }); // 모여드는 바람

    // 도는 빠르기: 터진 직후 빠르다가 풀리고, 모여들수록 점점 빨라진다
    let w = W0;
    if (c < FLING) w = W0 + (W_FLING - W0) * (1 - c / FLING) ** 2;
    else if (c >= g0) w = W0 + (WMAX - W0) * smooth((c - g0) / GATHER);
    const a0 = this.angle;
    this.angle += w * dt;
    // 회오리가 한 자리를 지날 때마다 "휘잉" — 모여들수록 잦아진다
    if (Math.floor(this.angle / QUARTER) !== Math.floor(a0 / QUARTER)) {
      audio.play('whirl', { rate: 0.8 + (Math.floor(this.angle / QUARTER) % 4) * 0.07, gain: 0.75 });
    }

    // 조화! — 모였던 자리에서 터진다
    if (burst) hits += this.burst(host, p, R);

    const st = p.status;
    const stOf: Record<Gust, StatusApply> = {
      fire: { burn: st.burn, burnLock: st.burnLock, burnDmg: st.burnDmg },
      ice: { frost: st.frost },
      bolt: { shock: st.shock },
      sand: {},
    };
    this.pullT -= dt;
    const pull = this.pullT <= 0 && c > FLING * 0.5 && c < g0;
    if (pull) this.pullT = 0.3;

    for (const t of this.twisters) {
      const a = this.angle + t.i * QUARTER;
      const r = this.orbitR(t.i, c, P, R);
      t.x = host.px + Math.cos(a) * r;
      t.y = host.py + Math.sin(a) * r;
      // 피해 — 한 적은 cd 초에 한 번 (넷이 같은 칸을 써서, 여러 회오리가 한 적을 한꺼번에 여러 번 치지 않는다)
      const sand = t.el === 'sand';
      // 불·얼음·번개 회오리에 닿은 적은 휘말려 떠올라 한 바퀴 돌며 튕겨 나간다(날아가는 쪽은 곧 받을 넉백).
      // 치기 전에 띄운다 — 이 한 방에 죽는 적도 휘말린 채 죽어 시체가 날아간다
      if (!sand) host.liftEnemies(t.x, t.y - 6, p.size, GUST_LIFT_H, GUST_LIFT_T, 1, GUST_LIFT_CD);
      hits += host.areaDamage(
        t.x, t.y - 6, p.size, p.dmg * TWISTER_MUL, sand ? 0 : p.knockback * 0.5,
        sand ? 0.7 : 0, sand ? 0.35 : 0, p.slot, p.cd, stOf[t.el],
      );
      // 모래 깔때기: 둘레의 적을 빨아들인다 (모여들 때는 안 한다 — 캐릭터 쪽으로 끌고 오게 되니까)
      if (sand && pull) host.pullEnemies(t.x, t.y, p.size * 2.1, p.size * 0.35);
      this.drawTwister(t, dt, c, P, w, r, a);
      this.drawWind(t, dt, c, P, w);
    }

    this.updateGround(dt, c, P);
    this.updateAir(dt, c, P);
    this.updateDevils(dt, c, P, R, w);
    return hits;
  }

  /** 회오리 i 의 궤도 반경 — 평소엔 숨 쉬듯(마주 보는 둘씩 번갈아), 모일 때 안으로, 터진 뒤 밖으로 튀었다 제자리로 */
  private orbitR(i: number, c: number, P: number, R: number): number {
    // 마주 보는 둘이 안으로 파고들 때 나머지 둘은 밖으로 — 네 잎 꽃처럼. 몸에 붙은 적도 1.5초마다 쓸고 지나간다
    const breathe = R * (1 + BREATHE * Math.sin(this.time * 2.1 + i * QUARTER));
    if (c < FLING) {
      const u = c / FLING;
      if (u < 0.3) return IN_R + (R * 1.22 - IN_R) * easeOut(u / 0.3);
      return R * 1.22 + (breathe - R * 1.22) * smooth((u - 0.3) / 0.7);
    }
    const g0 = P - GATHER;
    if (c < g0) return breathe;
    return breathe + (IN_R - breathe) * smooth((c - g0) / (GATHER - CHARGE));
  }

  /**
   * 깔때기 몸통. 띠마다 가로로 밀어 발끝은 땅에 박고 머리 쪽이 휜다:
   * 몸통을 따라 올라가는 흔들림 + 도는 쪽 반대로 머리가 끌림 + 모여들 때는 머리가 캐릭터 쪽으로 기운다(넷이 왕관처럼).
   */
  private drawTwister(t: Twister, dt: number, c: number, P: number, w: number, r: number, a: number): void {
    const h = this.host;
    const g0 = P - GATHER;
    const gathering = c >= g0;
    const gu = gathering ? smooth((c - g0) / (GATHER - CHARGE)) : 0;
    const variant = c < 0.55 ? 2 : gathering ? 1 : 0;
    const list = columns.get(t.el)![variant];
    t.spinT += dt * (gathering || c < FLING * 0.6 ? 26 : 16);
    const f = list[Math.floor(t.spinT) % list.length];
    // 크기: 모여 돌 때 오므라들고(캐릭터를 덜 가리게), 터질 때 부풀었다 돌아온다. 평소엔 살짝 고동친다
    let scale = this.vis * (1 + 0.03 * Math.sin(this.time * 9 + t.i * 1.3));
    if (gathering) scale *= 1 - 0.24 * gu;
    else if (c < FLING) scale *= c < 0.25 ? 0.78 + 0.34 * easeOut(c / 0.25) : 1.12 - 0.12 * smooth((c - 0.25) / (FLING - 0.25));
    const x = t.x;
    const y = t.y;
    t.h = f.h * scale;
    t.rTop = (f.w / 2) * scale;
    // 흔들림 크기(머리에서, px) · 도는 쪽 반대로 끌림 · 모일 때 캐릭터 쪽으로 기울기
    const sway = (gathering ? 5 : c < FLING ? 9 : 6) * scale;
    const vt = -Math.sin(a) * r * w; // 캐릭터 기준 가로 속도
    const lean = Math.max(-16, Math.min(16, -vt * 0.028)) * scale;
    const inward = (h.px - x) * 0.38 * gu;
    // 캐릭터 앞(화면 아래)에서 캐릭터를 가리면 비쳐 보이게
    const cover = y > h.py + 4 && Math.abs(x - h.px) < t.rTop && y - h.py < t.h * 0.9;
    t.alpha = cover ? 0.5 : 1;
    // 띠의 윗변을 정수 px 에 두고, 띠 높이는 다음 띠 윗변까지 딱 맞게 늘인다(띠 사이에 틈이 안 생기게)
    const topY = (b: number): number => Math.round(y + ((b < BANDS ? f.bands[b].top : f.h) - f.ay) * scale);
    t.bend.length = 0;
    for (let b = 0; b < BANDS; b++) {
      const sp = t.bands[b];
      const band = f.bands[b];
      if (sp.texture !== band.tex) sp.texture = band.tex;
      sp.anchor.set(f.ax / f.w, 0);
      const up = 1 - (band.top + band.tex.frame.height / 2) / f.h; // 이 띠의 높이 (발끝 0 → 머리 1)
      const off =
        Math.sin(this.time * 3.1 + t.i * 1.7 - up * 2.3) * sway * up ** 1.3 + lean * up ** 1.15 + inward * up ** 1.6;
      t.bend.push(off);
      const y0 = topY(b);
      const bh = band.tex.frame.height;
      sp.scale.set(scale, bh > 0 ? (topY(b + 1) - y0) / bh : scale);
      sp.position.set(Math.round(x + off), y0);
      sp.zIndex = y;
      sp.alpha = t.alpha;
      sp.visible = true;
    }
    // 발끝: 도는 소용돌이 원반 + 옅은 그림자
    const base = baseArt.get(t.el);
    if (base?.length) {
      t.baseT += dt * (gathering ? 30 : 20);
      const bf = base[Math.floor(t.baseT) % base.length];
      const bs = t.base;
      if (bs.texture !== bf.tex) bs.texture = bf.tex;
      bs.anchor.set(bf.ax, bf.ay);
      const bw = Math.max(1, bf.tex.frame.width);
      const bh = Math.max(1, bf.tex.frame.height);
      // 넓고 납작하고 반투명하게 — 발끝이 흙먼지 속에 묻힌다 (좁고 짙으면 잔 받침처럼 보였다)
      const wpx = 86 * scale;
      bs.scale.set(wpx / bw, (wpx * 0.36) / bh);
      bs.position.set(Math.round(x), Math.round(y + 1));
      bs.alpha = 0.62 * t.alpha;
      bs.visible = true;
    }
    t.shadow.position.set(Math.round(x), Math.round(y));
    t.shadow.scale.set(scale, scale);
    // 밑동에서 이따금 튀는 불티·서리·불똥·모래 바람
    t.puff -= dt;
    if (t.puff <= 0) {
      t.puff = gathering ? 0.22 : 0.5;
      const pf = PUFF[t.el];
      h.burst(x + (Math.random() - 0.5) * 30 * scale, y - 6 - Math.random() * 14, 13 + Math.random() * 7, pf.attr, pf.style, RAMP[t.el]);
    }
  }

  /** 몸통을 감는 바람 줄 셋과 휘말린 파편 — 몸통 뒤쪽 반은 옅게 뒤에(back), 앞쪽 반은 짙게 앞에(front) */
  private drawWind(t: Twister, dt: number, c: number, P: number, w: number): void {
    const back = t.back;
    const front = t.front;
    back.clear();
    front.clear();
    back.zIndex = t.y - 0.5;
    front.zIndex = t.y + 0.5;
    const gathering = c >= P - GATHER;
    const tint = TINT[t.el];
    // 몸통 위의 한 점 (높이 up, 둘레 각 th, 반지름 배율 rk) — 띠의 휨을 따라간다
    const bendAt = (up: number): number => {
      const n = t.bend.length;
      if (!n) return 0;
      const fb = (1 - up) * (n - 1);
      const b0 = Math.max(0, Math.min(n - 1, Math.floor(fb)));
      const b1 = Math.min(n - 1, b0 + 1);
      return t.bend[b0] + (t.bend[b1] - t.bend[b0]) * (fb - b0);
    };
    const at = (up: number, th: number, rk: number): [number, number] => {
      const rr = t.rTop * funnelK(up) * rk;
      return [t.x + bendAt(up) + Math.cos(th) * rr, t.y - up * t.h * 0.97 + Math.sin(th) * rr * 0.3];
    };
    const fade = c < 0.3 ? c / 0.3 : 1;
    const alphaK = t.alpha * fade;

    // 바람 줄: 머리(앞)가 굵고 밝고 꼬리로 갈수록 가늘고 옅다.
    // 토막마다 따로 그으면 한 장면에 수백 번 긋게 돼서(그리기 1ms 넘게 늘었다) — 같은 쪽(앞/뒤)·같은 짙기 단계로 이어진 토막은 한 줄로 긋는다
    t.wispA += dt * (7 + 3 * (w / W0));
    const N_SEG = 12;
    const LEVELS = 3;
    for (let k = 0; k < WISPS.length; k++) {
      const wd = WISPS[k];
      const head = t.wispA * wd.speed + k * 2.1 + t.i;
      const up = wd.up + 0.03 * Math.sin(this.time * 2 + k);
      const rk = 1.22 + (gathering ? 0.12 : 0);
      let run: number[] = [];
      let runKey = -1;
      const flush = (): void => {
        if (run.length >= 4 && runKey >= 0) {
          const inFront = runKey >= LEVELS;
          const wgt = ((runKey % LEVELS) + 1) / LEVELS; // 이 단계의 짙기 (머리 쪽이 1)
          const al = (inFront ? 0.9 : 0.38) * wgt * alphaK;
          if (al >= 0.03) {
            // 앞쪽은 빛 번짐 + 심지 두 겹, 몸통 뒤로 도는 쪽은 옅은 한 겹
            const g = inFront ? front : back;
            if (inFront) g.poly(run, false).stroke({ width: 1 + 3.2 * wgt, color: tint[2], alpha: al * 0.55, cap: 'round', join: 'round' });
            g.poly(run, false).stroke({ width: 0.8 + 1.4 * wgt, color: tint[inFront ? 0 : 1], alpha: al, cap: 'round', join: 'round' });
          }
        }
        run = [];
      };
      for (let j = 0; j <= N_SEG; j++) {
        const u = j / N_SEG;
        const th = head - wd.span * (1 - u);
        const [x, y] = at(up + 0.05 * u, th, rk);
        if (j === N_SEG) {
          run.push(x, y);
          break;
        }
        // 이 토막(j → j+1)의 쪽과 짙기 단계
        const midTh = head - wd.span * (1 - (j + 0.5) / N_SEG);
        const lvl = Math.min(LEVELS - 1, Math.floor(((j + 1) / N_SEG) ** 1.4 * LEVELS));
        const key = (Math.sin(midTh) > 0 ? LEVELS : 0) + lvl;
        if (key !== runKey) {
          if (run.length) {
            run.push(x, y); // 앞 토막을 이 점까지 이어 끊김 없이
            flush();
          }
          runKey = key;
        }
        run.push(x, y);
      }
      flush();
    }

    // 파편: 발끝 가까이서 휘말려 나선으로 오르다가 끝에서 바깥으로 튕겨 나간다
    t.debrisAcc += dt * DEBRIS_RATE * (gathering ? 1.5 : 1);
    while (t.debrisAcc >= 1) {
      t.debrisAcc -= 1;
      if (t.debris.length >= DEBRIS_MAX) break;
      const cols = DEBRIS[t.el];
      if (Math.random() < SKIRT) {
        // 발끝 흙먼지 치마: 발끝 반지름의 두세 배로 납작하게 빠르게 돈다
        t.debris.push({
          th: Math.random() * TAU, up: Math.random() * 0.06, rk: 2.4 + Math.random() * 1.8,
          w: 9 + Math.random() * 5, vu: 0.04, vr: 0.3, t: 0, life: 0.45 + Math.random() * 0.35,
          color: cols[1 + Math.floor(Math.random() * 3)], size: Math.random() < 0.4 ? 2 : 1, skirt: true,
        });
        continue;
      }
      const big = t.el === 'sand' ? Math.random() < 0.2 : t.el === 'ice' ? Math.random() < 0.12 : false;
      t.debris.push({
        th: Math.random() * TAU,
        up: Math.random() * 0.3,
        rk: 1.35 + Math.random() * 0.55, // 몸통 바깥에서 돈다 — 몸통 위에 겹치면 같은 빛깔이라 안 보였다
        w: 6 + Math.random() * 5,
        vu: 0.5 + Math.random() * 0.6,
        vr: 0.25 + Math.random() * 0.5,
        t: 0,
        life: 0.75 + Math.random() * 0.6,
        color: big ? cols[3] : cols[Math.floor(Math.random() * 3)],
        size: big ? 3 : Math.random() < 0.5 ? 2 : 1,
        skirt: false,
      });
    }
    // 알갱이는 같은 쪽·빛깔·짙기 단계끼리 모아 한 번에 칠한다 (하나씩 칠하면 수백 번)
    const buckets = new Map<number, { g: Graphics; color: number; alpha: number; rects: number[] }>();
    for (let k = t.debris.length - 1; k >= 0; k--) {
      const q = t.debris[k];
      q.t += dt;
      if (q.t >= q.life) {
        t.debris.splice(k, 1);
        continue;
      }
      const u = q.t / q.life;
      q.th += q.w * dt * (w / W0) * 0.7 + q.w * dt * 0.3;
      q.up = Math.min(1.05, q.up + q.vu * dt);
      // 마지막 35% 는 바깥으로 튕겨 나간다 (치마는 조금만 퍼진다)
      if (u > 0.65) q.rk = Math.min(q.skirt ? 5 : 3.6, q.rk + q.vr * (q.skirt ? 2 : 6) * dt);
      const [x, y] = at(q.up, q.th, q.rk);
      const inFront = Math.sin(q.th) > 0;
      const g = inFront ? front : back;
      const al = (inFront ? 1 : 0.45) * (q.skirt ? 0.7 : 1) * (u < 0.12 ? u / 0.12 : u > 0.8 ? (1 - u) / 0.2 : 1) * alphaK;
      const step = Math.round(al * 4); // 짙기 다섯 단계
      if (step <= 0) continue;
      const key = (inFront ? 1 : 0) * 0x10000000 + q.color * 8 + step;
      let b = buckets.get(key);
      if (!b) {
        b = { g, color: q.color, alpha: step / 4, rects: [] };
        buckets.set(key, b);
      }
      b.rects.push(Math.round(x), Math.round(y), q.size + 1);
    }
    for (const b of buckets.values()) {
      for (let k = 0; k < b.rects.length; k += 3) b.g.rect(b.rects[k], b.rects[k + 1], b.rects[k + 2], b.rects[k + 2]);
      b.g.fill({ color: b.color, alpha: b.alpha });
    }
  }

  /** 바닥: 모여들 때 넷을 잇는 네 빛깔 고리 + 조화가 터질 때 퍼지는 고리. (돌풍 자국은 뺐다 — 발끝 옆에 어두운 쐐기 그림자처럼 보였다) */
  private updateGround(dt: number, c: number, P: number): void {
    const h = this.host;
    const g = this.ground;
    g.clear();
    // 모여드는 동안: 넷을 잇는 네 빛깔 고리가 짙어진다
    const g0 = P - GATHER;
    if (c >= g0) {
      const u = smooth((c - g0) / GATHER);
      const rr = this.twisters.reduce((s, t) => s + Math.hypot(t.x - h.px, t.y - h.py), 0) / 4;
      for (const t of this.twisters) {
        const a = this.angle + t.i * QUARTER;
        const pts = arcPts(h.px, h.py, rr, rr, a - QUARTER / 2, a + QUARTER / 2, 14);
        g.poly(pts, false).stroke({ width: 2 + 5 * u, color: TINT[t.el][1], alpha: 0.2 + 0.55 * u });
      }
    }
    // 조화 고리: 가운데서 네 빛깔로 퍼져 나간다
    if (this.ringT >= 0) {
      this.ringT += dt;
      const u = this.ringT / 0.45;
      if (u >= 1) this.ringT = -1;
      else {
        const rr = IN_R + (this.ringR - IN_R) * easeOut(u);
        const k = 1 - u;
        for (const t of this.twisters) {
          const a = this.angle * 1.5 + t.i * QUARTER;
          const pts = arcPts(h.px, h.py, rr, rr, a - QUARTER / 2, a + QUARTER / 2, 18);
          g.poly(pts, false).stroke({ width: 3 + 12 * k, color: TINT[t.el][2], alpha: 0.75 * k });
        }
        g.poly(arcPts(h.px, h.py, rr * 0.97, rr * 0.97, 0, TAU, 48), true).stroke({ width: 2, color: 0xfffaf0, alpha: 0.8 * k });
      }
    }
  }

  /**
   * 허공: 모여드는 동안 번개가 번개 회오리 머리에서 나머지 셋의 머리로 튄다 — 매 장면 지금 자리로 다시 그어서 떨어져 떠 있지 않는다.
   * 꺾이는 모양만 0.06초마다 바꾼다.
   */
  private updateAir(dt: number, c: number, P: number): void {
    const g = this.air;
    g.clear();
    const g0 = P - GATHER;
    if (c < g0) return;
    const u = smooth((c - g0) / GATHER);
    this.arcT -= dt;
    if (this.arcT <= 0 || this.arcShape.length === 0) {
      this.arcT = 0.06;
      this.arcShape = [0, 1, 2].map(() => Array.from({ length: 7 }, () => Math.random() * 2 - 1));
    }
    const bolt = this.twisters.find((t) => t.el === 'bolt');
    if (!bolt) return;
    const top = (t: Twister): [number, number] => [t.x + (t.bend[0] ?? 0), t.y - t.h * 0.86];
    const [bx, by] = top(bolt);
    let s = 0;
    for (const o of this.twisters) {
      if (o === bolt) continue;
      const [ox, oy] = top(o);
      const shape = this.arcShape[s++];
      const dx = ox - bx;
      const dy = oy - by;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;
      const pts: number[] = [bx, by];
      for (let k = 0; k < shape.length; k++) {
        const f = (k + 1) / (shape.length + 1);
        const j = shape[k] * Math.min(22, len * 0.16) * Math.sin(Math.PI * f);
        pts.push(bx + dx * f + nx * j, by + dy * f + ny * j);
      }
      pts.push(ox, oy);
      g.poly(pts, false).stroke({ width: 5, color: 0xffd23a, alpha: 0.35 * u, join: 'round' });
      g.poly(pts, false).stroke({ width: 1.8, color: 0xffffff, alpha: 0.9 * u, join: 'round' });
    }
  }

  /** 작은 흙먼지 회오리 — 폭풍 둘레 여기저기서 일었다 사라진다 (그림만) */
  private updateDevils(dt: number, c: number, P: number, R: number, w: number): void {
    const h = this.host;
    this.devilT -= dt;
    if (this.devilT <= 0 && c >= FLING * 0.5 && c < P - GATHER * 0.5) {
      this.devilT = 0.3;
      const d = this.devils.find((v) => v.life <= 0);
      if (d) {
        const el = GUSTS[Math.floor(Math.random() * 4)];
        const pieces = devilArt.get(el) ?? [];
        if (pieces.length) {
          d.frames = pieces[Math.floor(Math.random() * pieces.length)];
          const a = Math.random() * TAU;
          const rr = R * (0.5 + Math.random() * 0.95);
          d.x = h.px + Math.cos(a) * rr;
          d.y = h.py + Math.sin(a) * rr;
          // 폭풍과 같은 쪽으로 흘러간다
          d.vx = -Math.sin(a) * rr * w * 0.35;
          d.vy = Math.cos(a) * rr * w * 0.35;
          d.t = 0;
          d.life = 0.55 + Math.random() * 0.3;
          d.scale = 0.75 + Math.random() * 0.3;
        }
      }
    }
    for (const d of this.devils) {
      if (d.life <= 0) {
        d.sprite.visible = false;
        continue;
      }
      d.t += dt;
      if (d.t >= d.life) {
        d.life = 0;
        d.sprite.visible = false;
        continue;
      }
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      const f = d.frames[Math.floor(d.t * 18) % d.frames.length];
      const sp = d.sprite;
      if (sp.texture !== f.tex) sp.texture = f.tex;
      sp.anchor.set(f.ax, f.ay);
      sp.position.set(Math.round(d.x), Math.round(d.y));
      sp.scale.set(d.scale);
      const u = d.t / d.life;
      sp.alpha = (u < 0.2 ? u / 0.2 : u > 0.65 ? (1 - u) / 0.35 : 1) * 0.85;
      sp.visible = true;
    }
  }

  /** 조화 — 넷이 모인 자리에서 한꺼번에 터진다: 둘레 전부에 불·서리·전하를 한꺼번에, 멀리 날려 보낸다 */
  private burst(h: WeaponHost, p: TempestParams, R: number): number {
    const BR = R * BURST_R;
    // 둘레 전부가 폭풍에 휩쓸려 높이 떠올라 두 바퀴 돌며 바깥으로 날아간다 — 치기 전에 띄워야 이 폭발에 죽는 적도 날아간다
    h.liftEnemies(h.px, h.py, BR, BURST_LIFT_H, BURST_LIFT_T, 2, 0);
    const hits = h.areaDamage(h.px, h.py, BR, p.dmg * BURST_MUL, p.knockback * 2.2, 0, 0, -1, 0, p.status);
    for (const t of this.twisters) {
      const pf = PUFF[t.el];
      h.burst(t.x, t.y - 30, 56, pf.attr, t.el === 'fire' ? 'fire' : t.el === 'sand' ? 'air' : 'spark', RAMP[t.el]);
      // 네 빛깔이 바깥으로 흩날린다
      const a = this.angle + t.i * QUARTER;
      h.burst(h.px + Math.cos(a) * BR * 0.7, h.py + Math.sin(a) * BR * 0.7, 34, pf.attr, pf.style, RAMP[t.el], 0.08);
      // 몸통에 휘말려 있던 파편이 한꺼번에 튕겨 나간다
      for (const q of t.debris) {
        q.t = Math.max(q.t, q.life * 0.66);
        q.vr *= 1.6;
      }
    }
    h.burst(h.px, h.py - 20, BR * 0.55, 'white', 'air', FX_RAMPS.fog);
    this.ringT = 0;
    this.ringR = BR;
    h.flashScreen(0.12, 0.18, 0xfff8ec);
    h.shakeScreen(0.22, 6);
    h.heavyHit();
    h.playAttackPose('cast');
    audio.play('windBlast', { rate: 0.7, gain: 1.4 });
    audio.play('boom', { rate: 0.8 });
    return hits;
  }
}
